import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { iniciais, primeiroNome } from '../acesso';
import type { Eu } from '../acesso';
import { FaixaDeConexao } from '../design/Estados';
import { FotoOuIniciais } from '../design/FotoDePerfil';
import { Icone } from '../design/Icone';
import { BuscaGlobal } from './BuscaGlobal';
import { Sino } from './Sino';
import {
  ContaNaLateral,
  DicaDoTrilho,
  GrupoDaLateral,
  ItemDaLateral,
  TopoDaLateral,
  UnidadeAtual,
  ZonaDaLateral,
  useDicaDoTrilho,
} from './Lateral';
import {
  ITENS_DE_SISTEMA,
  MENU,
  MENU_DO_AUDITOR,
  MENU_DO_RECEPTOR,
  itemDaRota,
  rotaExiste,
} from './navegacao';
import type { GrupoDoMenu } from './navegacao';
import './shell.css';
import './lateral.css';

/**
 * TELA 02 — SHELL DO ADMINISTRADOR. "O shell ocupa a tela inteira. Sempre."
 *
 * Sidebar fixa + conteúdo fluido, sem max-width global. Os breakpoints são os do design:
 *   ≥1600  sidebar 240 · topbar 56 · padding 32
 *   1280–1599  sidebar 240 · topbar 48 · padding 20
 *   1024–1279  rail de 68 com tooltip · padding 16
 *   <1024  drawer sob demanda (botão de menu na topbar)
 * O balconista não usa este shell: o dele é mobile-first, com bottom navigation (Bloco 7).
 *
 * 🚨 Nada aqui inventa dado. O design desenha "Nicolas Silva · Administrador", a loja
 * "Drogaria do Atacado" e badges de contagem; aqui só aparece o que existe:
 *   - identidade: nome, iniciais e perfil vêm de `/api/me` (Bloco 2). Enquanto não chegam, a
 *     área da conta diz "Sua conta" — nunca um nome de exemplo;
 *   - menu: item com capacidade só aparece para quem tem (conveniência; o servidor recusa o resto);
 *   - loja: a Rede Inova de Muriaé, nunca o nome de outra farmácia;
 *   - sino e badges: sem ponto vermelho enquanto não houver notificação de verdade.
 */

export function Shell({
  rota,
  eu,
  pendentes,
  aoSair,
  children,
}: {
  rota: string;
  /** Quem está logado. `null` enquanto `/api/me` não respondeu. */
  eu: Eu | null;
  /** Pedidos de acesso aguardando decisão (só para quem aprova). `null` = não se sabe. */
  pendentes: number | null;
  aoSair: () => void;
  children: ReactNode;
}) {
  // O receptor (0058) tem o menu dele: só o Recebimento — nada do resto do InovaPay.
  // Quem recebe a mercadoria e não tem o módulo inteiro (o receptor; ou o auditor que também
  // recebe, como o Iggor — dono, 02/10): o Recebimento é item do menu dele, não aba de Encomendas.
  const recebeSo =
    eu !== null &&
    eu.capacidades.includes('encomendas.receber') &&
    !eu.capacidades.includes('encomendas.ver_todas') &&
    !eu.capacidades.includes('vendas.registrar');
  const receptor = recebeSo && !(eu?.capacidades.includes('giro.auditar') ?? false);
  // Para quem tem o módulo inteiro, o Recebimento é uma aba de Encomendas (acende Encomendas).
  const atual =
    !recebeSo && rota === 'encomendas-recebimento'
      ? (ITENS_DE_SISTEMA[rota] ?? itemDaRota(rota))
      : itemDaRota(rota);
  const [recolhida, setRecolhida] = useState(() => lerPreferencia());
  const [drawer, setDrawer] = useState(false);
  const [contaAberta, setContaAberta] = useState(false);
  // o menu da conta abre junto de quem o chamou: o rodapé da lateral ou o avatar da topbar
  const [contaDaLateral, setContaDaLateral] = useState(false);
  // 1024–1279: sempre trilho (sem espaço para 240px); <1024: gaveta, sempre aberta por inteiro
  const railForcado = useMidia('(min-width: 1024px) and (max-width: 1279px)');
  const gaveta = useMidia('(max-width: 1023px)');
  const trilho = !gaveta && (recolhida || railForcado);
  const { dica, mostrar, esconder } = useDicaDoTrilho(trilho);
  const [busca, setBusca] = useState(false);
  const existe = rotaExiste(rota);
  const nome = eu ? eu.nomeExibicao || eu.nome : null;
  const papel = eu ? eu.perfis.map((p) => p.nome).join(' · ') || 'Sem perfil' : null;
  const visivel = (capacidade?: string) =>
    capacidade === undefined || (eu?.capacidades.includes(capacidade) ?? false);
  // O auditor tem o menu dele (tela 22): audita, não administra.
  const auditor =
    eu !== null &&
    eu.capacidades.includes('giro.auditar') &&
    !eu.capacidades.includes('giro.administrar');
  const menu = (
    receptor
      ? MENU_DO_RECEPTOR
      : auditor
        ? [...MENU_DO_AUDITOR, ...(recebeSo ? MENU_DO_RECEPTOR : [])]
        : MENU
  )
    .map((g) => ({
      ...g,
      itens: g.itens.filter((i) => visivel(i.capacidade)),
    }))
    .filter((g) => g.itens.length > 0);
  // o dono vê as duas zonas (loja + InovaPay); o administrador comum, só a do InovaPay
  const duasZonas = new Set(menu.map((g) => g.zona).filter(Boolean)).size > 1;
  const avatar = (tamanho: number) =>
    nome ? (
      <FotoOuIniciais
        foto={eu?.foto}
        nome={nome}
        semFoto={<span className="sh-iniciais">{iniciais(nome)}</span>}
      />
    ) : (
      <Icone nome="user" tamanho={tamanho} traco={1.6} />
    );

  useEffect(() => {
    // Trocar de página fecha o drawer e os menus: a pessoa escolheu para onde ir.
    setDrawer(false);
    setContaAberta(false);
  }, [rota]);

  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setBusca(true);
      }
      if (e.key === 'Escape') {
        setBusca(false);
        setDrawer(false);
        setContaAberta(false);
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, []);

  const alternarRecolhida = () => {
    setRecolhida((v) => {
      salvarPreferencia(!v);
      return !v;
    });
  };

  const menuDaConta = (
    <div className={`sh-painel sh-menu-conta${contaDaLateral ? ' sb-menu-conta' : ''}`} role="menu">
      <div className="sh-menu-conta-cabeca">
        <span className="sh-avatar" aria-hidden="true">
          {avatar(16)}
        </span>
        <div>
          <div className="sh-menu-conta-nome">{nome ?? 'Sua conta'}</div>
          <div className="sh-menu-conta-papel">
            {eu ? `${papel ?? ''} · cód ${eu.login}` : 'Carregando…'}
          </div>
        </div>
      </div>
      {/* Uma unidade só: o seletor de unidade não aparece em lugar nenhum (design 07) —
                  ninguém escolhe o que não tem opção. */}
      <a className="sh-menu-item" role="menuitem" href="#/conta">
        <Icone nome="user" tamanho={16} traco={1.5} />
        Minha conta
      </a>
      <a className="sh-menu-item" role="menuitem" href="#/preferencias">
        <Icone nome="cog" tamanho={16} traco={1.5} />
        Preferências
      </a>
      <button type="button" role="menuitem" className="sh-menu-item sh-menu-sair" onClick={aoSair}>
        <Icone nome="arrow" tamanho={16} traco={1.6} />
        Sair
      </button>
    </div>
  );

  return (
    <div className={`sh${trilho ? ' sh-recolhida' : ''}${drawer ? ' sh-drawer-aberto' : ''}`}>
      <aside className="sh-lateral sb" aria-label="Menu principal">
        <TopoDaLateral
          recolhida={trilho}
          podeRecolher={!railForcado}
          aoAlternar={alternarRecolhida}
          aoFechar={() => setDrawer(false)}
          aoDica={mostrar}
          aoSairDica={esconder}
        />
        <UnidadeAtual
          area={auditor ? 'Auditoria do Giro' : 'Painel de gestão'}
          aoDica={mostrar}
          aoSairDica={esconder}
        />

        <nav className="sb-nav" aria-label="Páginas" onScroll={esconder}>
          {menu.map((grupo, gi) => (
            <Fragment key={`${grupo.titulo ?? ''}${String(gi)}`}>
              {duasZonas && grupo.zona && menu[gi - 1]?.zona !== grupo.zona && (
                <ZonaDaLateral zona={grupo.zona} />
              )}
              <GrupoDaLateral titulo={tituloDoGrupo(grupo, gi)}>
                {grupo.itens.map((item) => (
                  // Em rota inexistente (404) nenhum item fica ativo: marcar a Visão geral seria
                  // dizer que a pessoa está onde ela não está.
                  <ItemDaLateral
                    key={item.rota}
                    rota={item.rota}
                    rotulo={item.rotulo}
                    icone={item.icone}
                    ativo={existe && item.rota === atual.rota}
                    contador={item.rota === 'aprovacoes' ? pendentes : null}
                    aoDica={mostrar}
                    aoSairDica={esconder}
                  />
                ))}
              </GrupoDaLateral>
            </Fragment>
          ))}
        </nav>

        <ContaNaLateral
          nome={nome}
          papel={papel}
          avatar={avatar(15)}
          aberta={contaAberta && contaDaLateral}
          aoAlternar={() => {
            setContaDaLateral(true);
            setContaAberta((v) => !(v && contaDaLateral));
          }}
          aoDica={mostrar}
          aoSairDica={esconder}
        />
        {/* chamado pelo rodapé, o menu vive DENTRO da lateral: na gaveta do celular ele ficava
            por baixo dela (a topbar está numa camada abaixo) */}
        {contaAberta && contaDaLateral && menuDaConta}
        <DicaDoTrilho dica={dica} />
      </aside>

      {drawer && <div className="sh-veu" onClick={() => setDrawer(false)} aria-hidden="true" />}

      <div className="sh-principal">
        <header className="sh-topo">
          <button
            type="button"
            className="sh-abrir-menu"
            onClick={() => setDrawer(true)}
            aria-label="Abrir menu"
          >
            <Icone nome="menu" tamanho={19} traco={1.6} />
          </button>
          {existe ? (
            <div className="sh-trilha">
              {/* Na Visão geral a trilha é "Visão geral › Hoje · 22/09/2026" (o contexto da página
                  é o dia); nas outras, "Grupo › Página". */}
              <span className="sh-trilha-grupo">
                {grupoDe(atual.rota, auditor) ?? atual.rotulo}
              </span>
              <span className="sh-trilha-seta" aria-hidden="true">
                <Icone nome="chev-r" tamanho={13} traco={1.7} />
              </span>
              <span className="sh-trilha-pagina">
                {grupoDe(atual.rota, auditor) === null ? (
                  <>
                    <span className="sh-trilha-longa">
                      Hoje · {new Date().toLocaleDateString('pt-BR')}
                    </span>
                    <span className="sh-trilha-curta">{atual.rotulo}</span>
                  </>
                ) : (
                  atual.rotulo
                )}
              </span>
            </div>
          ) : (
            <div className="sh-trilha sh-trilha-404">/{rota}</div>
          )}

          <button type="button" className="sh-busca" onClick={() => setBusca(true)}>
            <Icone nome="search" tamanho={15} traco={1.6} />
            <span className="sh-busca-texto">
              <span className="sh-busca-texto-longo">Buscar produto, EAN, pessoa ou tela…</span>
              <span className="sh-busca-texto-curto">Buscar…</span>
            </span>
            <span className="sh-atalho">Ctrl K</span>
          </button>

          <div className="sh-topo-direita">
            <span
              className="sh-loja-icone"
              title="Unidade: Rede Inova · Muriaé"
              aria-label="Unidade: Rede Inova · Muriaé"
            >
              <Icone nome="box" tamanho={16} traco={1.5} />
            </span>
            <span className="sh-loja" title="Unidade">
              Rede Inova · Muriaé
            </span>
            <Sino eu={eu} classe="sh-sino" painelClasse="sino-caixa" />
            <span className="sh-divisor" aria-hidden="true" />
            <button
              type="button"
              className="sh-conta-topo"
              onClick={() => {
                setContaDaLateral(false);
                setContaAberta((v) => !(v && !contaDaLateral));
              }}
              aria-expanded={contaAberta}
            >
              <span className="sh-avatar sh-avatar-claro" aria-hidden="true">
                {avatar(15)}
              </span>
              <span className="sh-conta-topo-nome">{nome ? primeiroNome(nome) : 'Sua conta'}</span>
            </button>
          </div>

          {contaAberta && !contaDaLateral && menuDaConta}
        </header>

        <main className="sh-conteudo" id="conteudo" tabIndex={-1}>
          <FaixaDeConexao />
          {children}
        </main>
      </div>

      {busca && <BuscaGlobal eu={eu} aoFechar={() => setBusca(false)} />}
    </div>
  );
}

/** O título do grupo na lateral. O primeiro do administrador não tem título no menu (a trilha
 * da Visão geral depende disso) — na lateral ele se chama "Visão geral". */
function tituloDoGrupo(grupo: GrupoDoMenu, indice: number): string {
  return grupo.titulo ?? (indice === 0 ? 'VISÃO GERAL' : '');
}

function useMidia(consulta: string): boolean {
  const [bate, setBate] = useState(() =>
    typeof window !== 'undefined' && 'matchMedia' in window
      ? window.matchMedia(consulta).matches
      : false,
  );
  useEffect(() => {
    if (!('matchMedia' in window)) return;
    const m = window.matchMedia(consulta);
    const mudou = () => setBate(m.matches);
    mudou();
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, [consulta]);
  return bate;
}

function grupoDe(rota: string, auditor = false): string | null {
  // quem não é auditor vê as abas da auditoria dentro do grupo da Divergências (CONTROLE)
  const doAuditor = MENU_DO_AUDITOR.some((g) => g.itens.some((i) => i.rota === rota));
  const noMenu = MENU.some((g) => g.itens.some((i) => i.rota === rota));
  const rotaDoGrupo = !auditor && doAuditor && !noMenu ? 'divergencias' : rota;
  const grupo = [
    ...(auditor ? MENU_DO_AUDITOR : MENU),
    ...MENU_DO_AUDITOR,
    ...MENU_DO_RECEPTOR,
  ].find((g) => g.itens.some((i) => i.rota === rotaDoGrupo));
  const t = grupo?.titulo;
  return t ? t.charAt(0) + t.slice(1).toLowerCase() : null;
}

const CHAVE = 'inovapay2.menu-recolhido';
function lerPreferencia(): boolean {
  try {
    return window.localStorage.getItem(CHAVE) === '1';
  } catch {
    return false;
  }
}
function salvarPreferencia(v: boolean) {
  try {
    window.localStorage.setItem(CHAVE, v ? '1' : '0');
  } catch {
    // janela anônima / storage bloqueado: a preferência só não é lembrada
  }
}
